import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Building2, User, Save, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";

export default function BillingForm() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const isFirst = params.get("first") === "1";
  const next = params.get("next") || "/profile";

  const [data, setData] = useState({
    entity_type: "private",
    legal_name: "",
    address: "",
    city: "",
    zip: "",
    country: "Slovensko",
    ico: "",
    dic: "",
    ic_dph: "",
    phone: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (user?.billing) {
      setData((d) => ({ ...d, ...user.billing }));
    } else if (user?.name) {
      setData((d) => ({ ...d, legal_name: user.name }));
    }
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login?next=/profile/billing" replace />;

  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setOk(false);
    try {
      await api.patch("/auth/billing", data);
      await refresh();
      setOk(true);
      setTimeout(() => nav(next, { replace: true }), 700);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Chyba pri ukladaní");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-10" data-testid="billing-page">
      <div className="mb-6">
        <span className="wb-tag !border-gold-600 !text-gold-500">FAKTURÁCIA</span>
        <h1 className="font-display font-black text-3xl md:text-4xl mt-2">Fakturačné údaje</h1>
        <p className="text-sm text-zinc-400 mt-2">
          {isFirst
            ? "Vyplň prosím údaje, na ktoré bude vystavená faktúra. Bez nich nebudeš môcť aktivovať predplatné."
            : "Tieto údaje sa použijú na všetky faktúry — predplatné aj inzeráty práce."}
        </p>
      </div>

      <form onSubmit={submit} className="space-y-4 border border-zinc-800 bg-[var(--wb-surface)] p-6" data-testid="billing-form">
        {/* Entity type toggle */}
        <div>
          <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">Typ subjektu</label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { v: "private", label: "Súkromná osoba", icon: User },
              { v: "company", label: "Firma / Živnostník", icon: Building2 },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                data-testid={`entity-${opt.v}`}
                onClick={() => setData({ ...data, entity_type: opt.v })}
                className={`flex items-center gap-2 px-4 py-3 text-sm border transition-colors ${
                  data.entity_type === opt.v
                    ? "border-gold-600 bg-gold-600/10 text-gold-300"
                    : "border-zinc-800 bg-zinc-900/40 text-zinc-400 hover:border-zinc-700"
                }`}
              >
                <opt.icon className="w-4 h-4" />
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <Field label={data.entity_type === "company" ? "Obchodné meno *" : "Meno a priezvisko *"}
          testid="legal-name" value={data.legal_name} onChange={set("legal_name")} required maxLength={160} />

        {data.entity_type === "company" && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="IČO *" testid="ico" value={data.ico} onChange={set("ico")} required maxLength={20} placeholder="napr. 57356050" />
            <Field label="DIČ" testid="dic" value={data.dic} onChange={set("dic")} maxLength={20} placeholder="napr. 2120000000" />
            <Field label="IČ DPH / VAT ID" testid="ic-dph" value={data.ic_dph} onChange={set("ic_dph")} maxLength={20} placeholder="napr. SK2120000000 / PL1234567890" />
          </div>
        )}
        {data.entity_type === "company" && <VatStatus saved={user.billing} current={data.ic_dph} />}

        <Field label="Adresa *" testid="address" value={data.address} onChange={set("address")} required maxLength={200} placeholder="Ulica a číslo" />
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Field label="Mesto *" testid="city" value={data.city} onChange={set("city")} required maxLength={80} />
          </div>
          <Field label="PSČ *" testid="zip" value={data.zip} onChange={set("zip")} required maxLength={20} placeholder="84105" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Krajina" testid="country" value={data.country} onChange={set("country")} maxLength={80} />
          <Field label="Telefón" testid="phone" value={data.phone} onChange={set("phone")} maxLength={40} placeholder="+421 …" />
        </div>

        {err && (
          <div className="flex items-center gap-2 p-2 border border-red-900 bg-red-950/30 text-red-400 text-sm" data-testid="billing-error">
            <AlertCircle className="w-4 h-4" /> {err}
          </div>
        )}
        {ok && (
          <div className="flex items-center gap-2 p-2 border border-green-900 bg-green-950/30 text-green-400 text-sm" data-testid="billing-ok">
            <CheckCircle2 className="w-4 h-4" /> Uložené — presmerúvam…
          </div>
        )}

        {/* GDPR consent */}
        <div className="border border-zinc-800 bg-zinc-950/50 p-3 text-[11px] leading-relaxed text-zinc-400" data-testid="gdpr-consent">
          <strong className="text-gold-400">Uložením osobných údajov vyjadrujem súhlas so spracovaním osobných údajov</strong> spoločnosťou SauberEko s. r. o. (IČO 57356050) v zmysle Nariadenia EÚ 2016/679 (GDPR) a zákona č. 18/2018 Z. z. na účely:
          (a) vystavenia faktúry, (b) plnenia zmluvy o poskytovaní služby Tigik, (c) komunikácie ohľadom predplatného a inzerátov.
          Údaje sa uchovávajú po dobu trvania zmluvy a 10 rokov od jej skončenia (zákonná povinnosť). Práva podľa GDPR (prístup, oprava, vymazanie): napíšte na <a href="mailto:info@saubereko.eu" className="text-gold-400 underline">info@saubereko.eu</a>.
        </div>

        <div className="flex items-center gap-3 pt-2 border-t border-zinc-800">
          <button
            type="submit"
            disabled={busy}
            data-testid="billing-save"
            className="wb-btn-primary inline-flex items-center gap-2 px-5 py-3 text-sm flex-1 justify-center"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {busy ? "Ukladám…" : "Uložiť údaje"}
          </button>
          {!isFirst && (
            <button type="button" onClick={() => nav(-1)} className="wb-btn-ghost px-4 py-3 text-sm">Späť</button>
          )}
        </div>
        <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 leading-relaxed">
          Údaje sú uložené v zabezpečenej databáze. Použité budú výlučne na vystavovanie faktúr cez SauberEko s.r.o. (IČO 57356050).
        </p>
      </form>
    </div>
  );
}

function VatStatus({ saved, current }) {
  const isSaved = saved?.ic_dph && saved.ic_dph === (current || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (!isSaved) {
    return (
      <p className="text-[11px] text-zinc-500 leading-relaxed" data-testid="vat-hint">
        Firmy z inej krajiny EÚ: zadaj IČ DPH s predponou krajiny (napr. PL…, CZ…, DE…). Po overení v registri EÚ (VIES) sa uplatní prenesenie daňovej povinnosti (reverse charge, 0 % DPH).
      </p>
    );
  }
  if (saved.vat_verified === true) {
    return (
      <div className="flex items-center gap-2 text-xs text-green-400" data-testid="vat-status-valid">
        <CheckCircle2 className="w-4 h-4" /> IČ DPH {saved.ic_dph} je overené v registri EÚ (VIES)
      </div>
    );
  }
  if (saved.vat_verified === false) {
    return (
      <div className="flex items-center gap-2 text-xs text-red-400" data-testid="vat-status-invalid">
        <AlertCircle className="w-4 h-4" /> IČ DPH {saved.ic_dph} nie je platné v registri EÚ (VIES) — faktúra bude s DPH
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 text-xs text-zinc-400" data-testid="vat-status-unknown">
      <AlertCircle className="w-4 h-4" /> Register VIES je momentálne nedostupný — IČ DPH {saved.ic_dph} skús uložiť znova neskôr
    </div>
  );
}

function Field({ label, testid, value, onChange, required, maxLength, placeholder, type = "text" }) {
  return (
    <div>
      <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={onChange}
        required={required} maxLength={maxLength} placeholder={placeholder}
        className="wb-input w-full px-3 py-2 text-sm" />
    </div>
  );
}
